import {
  ChevronDown,
  ChevronUp,
  Edit,
  TrashCan,
  UserMultiple
} from '@carbon/icons-react'
import { DDSToastNotification, DDSTypography } from 'den-design-system'
import {
  MediaFile,
  RichTextPayload
} from '../common/TextAreaDrafts/quill.types'
import React, { MutableRefObject, useEffect, useRef } from 'react'
import {
  addComment,
  deleteComment,
  getAllCommentsOfIncident,
  updateComment,
  updateCommentFormData
} from '../../api/incident/incident.service'
import { useParams, useSearchParams } from 'react-router-dom'

import { DeletePopup } from '../common/Exceptions/DeletePopup'
import { Delta } from 'quill'
import DocumentHeader from '../Helmet/DocumentHeader'
import { HeaderConstants } from '../../constants/HeaderConstants'
import ReadOnlyQuill from '../common/TextAreaDrafts/ReadOnlyQuill'
import RichTextEditor from '../common/TextAreaDrafts/RichTextEditor'
import Spinner from '../common/Spinner/Spinner'
import TextArea from '../common/DDS/TextArea/TextArea'
import { ToastMessageConstants } from '../../constants/ToastMessageConstants'
import ToastNotification from '../common/DDS/Toast/Toast'
import { getTimeForActivity } from '../../utils/DateTimeUtils'
import { isEmpty } from 'lodash'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { useState } from 'react'
import { useStoreContext } from '../../store/StoreContext'

const IncidentActivity: React.FC<{
  assignMemberOptions: any
  status: any
  fetchAnIncident: () => void
}> = ({ assignMemberOptions, fetchAnIncident, status }) => {
  const [payload, setPayload] = useState<RichTextPayload>({
    content: new Delta(),
    mediaFiles: [],
    isPublic: false
  })
  const [payloadUpdate, setPayloadUpdate] = useState<RichTextPayload>({
    content: new Delta(),
    mediaFiles: []
  })

  const { incidentId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false)
  const [activityMessages, setActivityMessages] = React.useState<any>()
  const [loading, setLoading] = useState<boolean>(true)
  const [buttonLoading, setButtonLoading] = useState<boolean>(false)
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])
  const store = useStoreContext()
  const [isPublic, setIsPublic] = useState<boolean>(false)
  const [filesToRemove, setFilesToRemove] = useState<string[]>([])
  const replySectionRef = useRef<{
    [key: string]: MutableRefObject<HTMLDivElement | null>
  }>({})
  const messageSectionRef: MutableRefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement>(null)

  const getReplySectionRef = (
    id: string
  ): MutableRefObject<HTMLDivElement | null> => {
    if (!replySectionRef.current[id]) {
      replySectionRef.current[id] = React.createRef()
    }
    return replySectionRef.current[id]
  }
  const currentUserId = store.userStore.getId()

  const updateMessages = (value: string) => {
    setText('')
    addComment({
      $incidentId: incidentId as string,
      comment: value,
      isPublic: isPublic
    })
      .then((response: any) => {
        fetchAnIncident()
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }

  const getUserInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || ''
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || ''
    return `${firstInitial}${lastInitial}`
  }

  const getTaggedUsers = (ops: Delta) => {
    const users = new Set<string>()

    ops.ops.forEach((op) => {
      if (typeof op.insert === 'object' && op.insert.mention) {
        const mention: any = op.insert.mention
        if (mention.id) {
          users.add(mention.id)
        }
      }
    })
    return Array.from(users)
  }

  const addQuill = (value: RichTextPayload) => {
    const formData = new FormData()
    const projectId = store.scopeStore.getSelectedProject().id
    const organizationId = store.scopeStore.getOrganizationId()

    formData.append('$incidentId', String(incidentId))
    formData.append('comment', JSON.stringify(value.content))
    formData.append('isPublic', String(value.isPublic))

    const taggedUsers = getTaggedUsers(new Delta(value.content))

    formData.append('taggedUser', JSON.stringify(taggedUsers))
    formData.append('projectId', projectId as string)
    formData.append('organizationId', organizationId as string)

    value.mediaFiles?.forEach((media: any) => {
      if (media.file) {
        formData.append('mediaFiles', media.file)
      }
    })

    addComment(formData)
      .then(() => {
        fetchAnIncident()
      })
      .catch((err: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.UPDATE_A_COMMENT.ERROR
        })
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }

  const saveQuill = (value: RichTextPayload, commentId: any) => {
    setLoading(true)
    setChangedEdit(false)

    const projectId = store.scopeStore.getSelectedProject().id
    const organizationId = store.scopeStore.getOrganizationId()
    const formData = new FormData()

    formData.append('$incidentId', String(incidentId))
    formData.append('$activityId', String(commentId))
    formData.append('message', JSON.stringify(value.content))

    const taggedUsers = getTaggedUsers(new Delta(value.content))
    formData.append('taggedUser', JSON.stringify(taggedUsers))
    formData.append('projectId', projectId as string)
    formData.append('organizationId', organizationId as string)
    if (filesToRemove !== null) {
      formData.append('filesToRemove', JSON.stringify(filesToRemove))
    }

    // ✅ Only append NEW uploaded files (not existing ones)
    const newFiles =
      value.mediaFiles?.filter((file: any) => file.isNew === true) || []

    newFiles.forEach((file: any) => {
      if (file?.file) {
        formData.append('files', file.file)
      }
    })

    updateCommentFormData(formData)
      .then(() => {
        setCurrentEditableMessage('')
        setEditableMessageId('')
        setEditableReplyId('')

        // Mark all files as no longer new after successful upload
        setPayloadUpdate((prev: any) => ({
          ...prev,
          mediaFiles: []
        }))

        setActivityMessages((prev: any[]) => {
          const updated = [...prev]
          const index = prev.findIndex((msg) => msg.id === activityMessages.id)

          if (index !== -1) {
            updated[index] = {
              ...prev[index]
            }
          }

          return updated
        })

        DDSToastNotification.success(
          ToastMessageConstants.INCIDENT.UPDATE_A_COMMENT.SUCCESS
        )
        fetchAllActivities()
      })
      .catch((err: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.UPDATE_A_COMMENT.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
        setButtonLoading(false)
      })
  }

  //Edit changes popup
  const [editChangesPopup, setEditChangesPopup] = useState<boolean>(false)
  //Hovering attributes for activity div
  const [hoveringDiv, setHoveringDiv] = useState<string>('')
  const [text, setText] = React.useState<string>('')

  //Editing messages
  const [editableMessageId, setEditableMessageId] = React.useState<string>('')
  const [changedEditId, setChangedEditId] = React.useState<string>('')
  const [changedEdit, setChangedEdit] = React.useState<boolean>(false)
  const [currentAttachedFiles, setCurrentAttachedFiles] = useState<string[]>([])

  const handleEditMessage = (id: string) => {
    setEditableMessageId(id)
    setChangedEdit(true)
    setCurrentEditableMessage(
      activityMessages.find((message: any, index: number) => message.id === id)
        .message
    )

    setTimeout(() => {
      const textAreaField = document.getElementById(id)
      if (textAreaField) {
        textAreaField.focus()
      }
    }, 0)
  }

  //Replying messages
  const [replyMessageId, setReplyMessageId] = React.useState<string>('')
  const [showReply, setShowReply] = useState<boolean>(false)
  const [showReplyId, setShowReplyId] = React.useState<string>('')
  const [currentReplyMessage, setCurrentReplyMessage] =
    React.useState<string>('')
  const [currentEditableReplyMessage, setCurrentEditableReplyMessage] =
    React.useState<string>('')
  const [editableReplyId, setEditableReplyId] = useState<string>('')
  const [changeReplyId, setChangeReplyId] = useState<string>('')

  const handleReplyMessage = (id: string) => {
    if (!isEmpty(replyMessageId) && !isEmpty(currentReplyMessage)) {
      setEditChangesPopup(true)
      setChangeReplyId(id)
    } else if (
      !isEmpty(editableMessageId) &&
      !isEmpty(currentEditableMessage)
    ) {
      setEditChangesPopup(true)
      setEditChangesPopup(true)
    } else {
      setReplyMessageId(id)
    }
  }

  useEffect(() => {
    if (showReplyId) {
      const ref = getReplySectionRef(showReplyId)
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [showReplyId])

  const handleContinueUnsavedChanges = (id: string) => {
    setDeleteLoading(true)
    setReplyMessageId(id)
    if (!isEmpty(editableMessageId)) {
      setEditableMessageId('')
    }
    setCurrentReplyMessage('')
    setDeleteLoading(false)
    setEditChangesPopup(false)
  }
  const handleShowReply = (id: string) => {
    const ref = getReplySectionRef(id)
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
    if (showReplyId === '') {
      setShowReplyId(id)
    } else {
      setShowReplyId('')
    }
  }
  const handleEditReplyMessage = (id: string, value: string) => {
    setEditableReplyId(id)
    setCurrentEditableReplyMessage(value)
    setTimeout(() => {
      const textAreaField = document.getElementById(id)
      if (textAreaField) {
        textAreaField.focus()
      }
    }, 0)
  }

  const handleCancelEditableReplyMessage = () => {
    setEditableReplyId('')
    setCurrentEditableReplyMessage('')
  }

  const saveMessage = (commentId: string, type: 'Parent' | 'Reply') => {
    setLoading(true)
    updateComment({
      message:
        type === 'Parent'
          ? (currentEditableMessage as string)
          : currentEditableReplyMessage,
      $incidentId: incidentId as string,
      $activityId: commentId,
      filesToRemove: filesToRemove
    })
      .then((response: any) => {
        setCurrentEditableMessage('')
        setEditableMessageId('')
        setEditableReplyId('')

        DDSToastNotification.success(
          ToastMessageConstants.INCIDENT.UPDATE_A_COMMENT.SUCCESS
        )
        fetchAllActivities()
      })
      .catch((err: any) => {
        ToastNotification({
          type: 'error',
          message: err
        })
        setLoading(false)
      })
  }

  const sendReply = () => {
    setLoading(true)
    addComment({
      $incidentId: incidentId as string,
      comment: currentReplyMessage,
      threadId: replyMessageId
    })
      .then((response: any) => {
        fetchAllActivities()
        ToastNotification({
          type: 'success',
          message: ToastMessageConstants.INCIDENT.ADD_A_COMMENT.SUCCESS
        })

        setCurrentReplyMessage('')
        setReplyMessageId('')
        setLoading(false)
      })
      .catch((err) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.ADD_A_COMMENT.ERROR
        })

        setLoading(false)
      })
  }

  const scrollToTop = () => {
    if (messageSectionRef.current) {
      messageSectionRef.current.scrollTop = 0
    }
  }
  const handleReplyMessageScroll = (id: string) => {
    const ref = getReplySectionRef(id)
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  useEffect(() => {
    scrollToTop()
  }, [activityMessages])

  const cancelReplyingMessage = () => {
    setReplyMessageId('')
    setCurrentReplyMessage('')
  }
  const cancelEditingMessage = () => {
    setEditableMessageId('')
    setChangedEdit(false)

    setPayloadUpdate({
      content: new Delta(),
      mediaFiles: []
    })

    setCurrentEditableMessage('')
    fetchAllActivities()
  }
  const fetchAllActivities = () => {
    setLoading(true)
    getAllCommentsOfIncident({ $incidentId: incidentId as string }).then(
      (response: any) => {
        setActivityMessages(response.data)

        setLoading(false)
      }
    )
  }

  const [deletePopupOpen, setDeletePopUpOpen] = useState<boolean>(false)
  const [deleteCommentContent, setDeleteCommentContent] = useState<any>({})

  const handleDeleteComment = (message: any) => {
    setDeletePopUpOpen(true)
    setDeleteCommentContent(message)
  }

  useEffect(() => {
    fetchAllActivities()
  }, [])
  const bannerDetails = { ...store.uiStore.getBannerDetails() }
  const isVisibility = store.uiStore.getIsAlertVisible()

  const [currentEditableMessage, setCurrentEditableMessage] =
    React.useState<string>('')

  return (
    <>
      <DocumentHeader header={HeaderConstants.INCIDENT} />
      <DeletePopup
        modalOpen={deletePopupOpen}
        setModalOpen={setDeletePopUpOpen}
        secondaryButtonClick={() => setDeletePopUpOpen(false)}
        primaryButtonClick={() => {
          setDeleteLoading(true)
          deleteComment({
            $incidentId: incidentId as string,
            $activityId: deleteCommentContent.id
          })
            .then(() => {
              fetchAllActivities()
              setDeleteLoading(false)
              setDeletePopUpOpen(false)
              ToastNotification({
                type: 'success',
                message: ToastMessageConstants.INCIDENT.DELETE_COMMENT.SUCCESS
              })
            })
            .catch((err) => {
              setDeleteLoading(false)
              ToastNotification({
                type: 'error',
                message: ToastMessageConstants.INCIDENT.DELETE_COMMENT.ERROR
              })
            })
        }}
        showFooter={true}
        primaryButtonText='Delete'
        secondaryButtonText='Cancel'
        primaryButtonDisabled={deleteLoading}
        deleteLoading={deleteLoading}
        secondaryButtonDisabled={deleteLoading}
        content={
          <div
            className='p-[24px] flex flex-col gap-y-[24px] border-b'
            style={{ borderColor: pallete.colors.stroke2 }}
          >
            <DDSTypography.Title
              type='h4'
              color={pallete.colors.textDark3}
              className='line-clamp-2 w-[600px]'
            >
              Are you sure you want to delete this message? This cannot be
              undone.
            </DDSTypography.Title>

            <div
              className='flex w-full flex-row p-[12px] gap-x-[12px] border rounded'
              style={{ borderColor: pallete.colors.stroke2 }}
            >
              <div className='h-[32px] w-[32px] rounded-full min-w-[32px] flex-shrink-0'>
                {deleteCommentContent.actionedByUser?.avatar ? (
                  <img
                    src={deleteCommentContent.actionedByUser?.avatar}
                    alt='icon'
                    className='h-full w-full object-cover rounded-full'
                  />
                ) : (
                  <div
                    className='rounded-full border aspect-square w-[32px] h-[32px] flex items-center justify-center bg-gradient-to-br '
                    style={{
                      borderColor: pallete.colors.stroke2
                    }}
                  >
                    <DDSTypography.Paragraph
                      size='para'
                      variant='bold'
                      color={pallete.colors.textDark3}
                      className='text-white text-sm font-semibold'
                      style={{
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      {getUserInitials(
                        deleteCommentContent.actionedByUser?.firstName,
                        deleteCommentContent.actionedByUser?.lastName
                      )}
                    </DDSTypography.Paragraph>
                  </div>
                )}
              </div>
              <div className='flex flex-col flex-1'>
                <div className='flex flex-row gap-x-[12px] items-center custom-text'>
                  <DDSTypography.Title type='h4' variant='semiBold'>
                    {deleteCommentContent.actionedByUser?.firstName +
                      ' ' +
                      deleteCommentContent.actionedByUser?.lastName}
                  </DDSTypography.Title>
                  <DDSTypography.Paragraph
                    size='caption'
                    variant='medium'
                    style={{ color: pallete.colors.textDark7 }}
                    className='whitespace-nowrap'
                  >
                    {getTimeForActivity(deleteCommentContent.createdAt)}
                  </DDSTypography.Paragraph>
                </div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  style={{
                    color: pallete.colors.textDark6,
                    overflowWrap: 'anywhere'
                  }}
                >
                  {deleteCommentContent.attachedFiles?.length > 0 && (
                    <div>
                      <DDSTypography.Paragraph
                        size='para'
                        variant='regular'
                        color={pallete.colors.textDark10}
                        className='pb-[8px]'
                        style={{
                          wordBreak: 'break-word'
                        }}
                      >
                        <span className=''>
                          {deleteCommentContent.actionedByUser?.firstName +
                            ' ' +
                            deleteCommentContent.actionedByUser?.lastName}
                        </span>{' '}
                        added attachment
                        {deleteCommentContent.attachedFiles.length > 1
                          ? 's'
                          : ''}
                        .
                      </DDSTypography.Paragraph>
                    </div>
                  )}
                  <ReadOnlyQuill
                    commentId={deleteCommentContent.id}
                    content={
                      deleteCommentContent.message?.includes('ops')
                        ? new Delta(JSON.parse(deleteCommentContent.message))
                        : new Delta().insert(
                            deleteCommentContent.message + '\n'
                          )
                    }
                    assignMemberOptions={assignMemberOptions}
                    incidentActivity={deleteCommentContent}
                  />
                </DDSTypography.Paragraph>
              </div>
            </div>
          </div>
        }
      />
      <DeletePopup
        modalOpen={editChangesPopup}
        setModalOpen={setEditChangesPopup}
        primaryButtonText='Continue'
        secondaryButtonText='Keep Editing'
        primaryButtonDisabled={deleteLoading}
        deleteLoading={deleteLoading}
        secondaryButtonDisabled={deleteLoading}
        primaryButtonClick={() => handleContinueUnsavedChanges(changeReplyId)}
        secondaryButtonClick={() => {
          setEditChangesPopup(false)
        }}
        showFooter={true}
        content={
          <div
            className='p-[24px] flex flex-col gap-y-[24px] border-b'
            style={{ borderColor: pallete.colors.stroke2 }}
          >
            <DDSTypography.Title
              type='h4'
              color={pallete.colors.textDark3}
              className='line-clamp-2 w-[600px]'
            >
              You will lose any unsaved changes to your message.
            </DDSTypography.Title>
          </div>
        }
      />
      <div
        className={`flex flex-col justify-between flex-shrink-0 gap-[24px] ${bannerDetails && isVisibility ? 'h-[69vh]' : 'h-[69vh]'}  py-[24px]  ${bannerDetails && isVisibility ? '2xl:h-[69vh]' : '2xl:h-[73vh]'} `}
      >
        {loading || store.uiStore.getGlobalLoader() ? (
          <div className='h-[60vh] 2xl:h-[70vh] '>
            <Spinner />
          </div>
        ) : (
          <div
            ref={messageSectionRef}
            className='flex flex-col overflow-y-auto h-[64vh] 2xl:h-[70vh] '
          >
            {activityMessages.map((message: any, index: number) => (
              <div
                key={index}
                className=' flex flex-col p-[8px] rounded-md'
                onMouseEnter={() => {
                  setHoveringDiv(message.id)
                }}
                onMouseLeave={() => {
                  setHoveringDiv('')
                }}
                style={{
                  backgroundColor:
                    hoveringDiv === message.id
                      ? pallete.colors.surface5
                      : 'white'
                }}
              >
                <div className={` flex flex-row gap-x-[12px] `}>
                  {message.actionedByUser ? (
                    message.actionedByUser.avatar ? (
                      <img
                        src={message.actionedByUser?.avatar}
                        alt='icon'
                        className='h-[32px] w-[32px] rounded-full'
                      />
                    ) : (
                      <div
                        className='rounded-full border aspect-square w-[32px] h-[32px] flex items-center justify-center bg-gradient-to-br '
                        style={{
                          borderColor: pallete.colors.stroke2
                        }}
                      >
                        <DDSTypography.Paragraph
                          size='para'
                          variant='bold'
                          color={pallete.colors.textDark3}
                          className='text-white text-sm font-semibold'
                          style={{
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          {getUserInitials(
                            message.actionedByUser?.firstName,
                            message.actionedByUser?.lastName
                          )}
                        </DDSTypography.Paragraph>
                      </div>
                    )
                  ) : null}

                  <div className='flex flex-col gap-y-[4px] justify-between w-full'>
                    {editableMessageId !== message.id ? (
                      <div className='flex flex-row justify-between w-full'>
                        <div className='flex flex-col gap-y-[4px] justify-between w-[100%]'>
                          <div className='flex flex-row items-center gap-x-[12px]'>
                            <div>
                              {message.actionedByUser ? (
                                <DDSTypography.Title
                                  type='h5'
                                  variant='semiBold'
                                  color={pallete.colors.textDark4}
                                >
                                  {message.actionedByUser?.firstName +
                                    ' ' +
                                    message.actionedByUser?.lastName}
                                </DDSTypography.Title>
                              ) : null}
                            </div>

                            <div>
                              <DDSTypography.Paragraph
                                size='caption'
                                variant='regular'
                                style={{ color: pallete.colors.textDark5 }}
                                className='whitespace-nowrap'
                              >
                                {getTimeForActivity(message.createdAt)}
                              </DDSTypography.Paragraph>
                            </div>

                            {message.isPublic && (
                              <UserMultiple
                                color={pallete.colors.textDark3}
                                style={{
                                  height: '12px',
                                  width: '12px'
                                }}
                              />
                            )}
                            {message.isEdited && (
                              <div>
                                <DDSTypography.Paragraph
                                  size='caption'
                                  variant='medium'
                                  style={{ color: pallete.colors.textDark5 }}
                                  className='whitespace-nowrap'
                                >
                                  {`(edited)`}
                                </DDSTypography.Paragraph>
                              </div>
                            )}
                          </div>
                          {message?.attachedFiles?.length > 0 && (
                            <div className='p-[12px]'>
                              <DDSTypography.Paragraph
                                size='para'
                                variant='medium'
                                color={pallete.colors.textDark10}
                                style={{ wordBreak: 'break-word' }}
                              >
                                <span className=''>
                                  {message.actionedByUser?.firstName +
                                    ' ' +
                                    message.actionedByUser?.lastName}
                                </span>{' '}
                                added attachment
                                {message.attachedFiles.length > 1 ? 's' : ''}.
                              </DDSTypography.Paragraph>
                            </div>
                          )}

                          <ReadOnlyQuill
                            commentId={message.id}
                            content={
                              message.message?.includes('ops')
                                ? new Delta(JSON.parse(message.message))
                                : new Delta().insert(message.message + '\n')
                            }
                            assignMemberOptions={assignMemberOptions}
                            incidentActivity={message}
                          />
                          {message?.data?.current === 'Resolved' &&
                            message.message
                              .toLowerCase()
                              .endsWith('resolved') && (
                              <div className='flex flex-col pl-[12px] gap-[8px]'>
                                {message?.rootCause && (
                                  <div>
                                    <DDSTypography.Paragraph
                                      size='para'
                                      variant='regular'
                                      color={pallete.colors.textDark10}
                                      style={{ wordBreak: 'break-word' }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          fontSize: '14px',
                                          color: pallete.colors.textDark5
                                        }}
                                      >
                                        Root Cause :
                                      </span>{' '}
                                      {message.rootCause}
                                    </DDSTypography.Paragraph>
                                  </div>
                                )}

                                {message?.solution && (
                                  <div>
                                    <DDSTypography.Paragraph
                                      size='para'
                                      variant='regular'
                                      color={pallete.colors.textDark10}
                                      style={{ wordBreak: 'break-word' }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          fontSize: '14px',
                                          color: pallete.colors.textDark5
                                        }}
                                      >
                                        Solution:
                                      </span>{' '}
                                      {message.solution}
                                    </DDSTypography.Paragraph>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                        {(hoveringDiv === message.id &&
                          message.type === 'COMMENT') ||
                        message.isActive ? (
                          <div className='flex flex-row gap-x-[8px]'>
                            {message.actionedByUser?.id === currentUserId && (
                              <>
                                <Edit
                                  color={pallete.colors.textDark10}
                                  className='cursor-pointer'
                                  onClick={() => {
                                    if (
                                      message.actionedByUser?.id ===
                                      currentUserId
                                    ) {
                                      handleEditMessage(message.id)
                                    }
                                  }}
                                />
                                <TrashCan
                                  color={pallete.colors.textDark10}
                                  className='cursor-pointer'
                                  onClick={() => {
                                    if (
                                      message.actionedByUser?.id ===
                                      currentUserId
                                    ) {
                                      handleDeleteComment(message)
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <RichTextEditor
                        placeholder='Update your reply'
                        variant='update'
                        content={
                          message.message?.includes('ops')
                            ? new Delta(JSON.parse(message.message))
                            : new Delta().insert(message.message + '\n')
                        }
                        attachmentFile={activityMessages}
                        setActivityMessages={setActivityMessages}
                        commentId={message.id}
                        changeEdit={changedEdit}
                        setChangeEdit={setChangedEdit}
                        payload={payloadUpdate}
                        isPublic={isPublic}
                        setIsPublic={setIsPublic}
                        setPayload={setPayloadUpdate}
                        setPayloadUpdate={setPayloadUpdate}
                        payloadUpdate={payloadUpdate}
                        loading={
                          buttonLoading ||
                          loading ||
                          store.uiStore.getGlobalLoader()
                        }
                        message={message}
                        disabled={loading}
                        assignMemberOptions={assignMemberOptions}
                        onCancel={cancelEditingMessage}
                        PrimaryButtonText='Save'
                        primaryButtonOnClick={(payload: any) => {
                          setButtonLoading(true)
                          saveQuill(payload, message.id)
                          scrollToTop()
                        }}
                        setFilesToRemove={setFilesToRemove}
                      />
                    )}
                    {message.type === 'COMMENT' &&
                      message.isActive &&
                      message.threads.length > 0 && (
                        <div onClick={() => handleShowReply(message.id)}>
                          <DDSTypography.Paragraph
                            size='caption'
                            variant='medium'
                            style={{ color: pallete.colors.textDark2 }}
                            className='whitespace-nowrap flex flex-row items-center cursor-pointer'
                          >
                            <div className='flex items-center'>
                              {showReplyId === '' ? (
                                <ChevronDown color={pallete.colors.textDark2} />
                              ) : (
                                <ChevronUp color={pallete.colors.textDark2} />
                              )}
                            </div>

                            {message.threads.length > 1
                              ? message.threads.length + ' Replies'
                              : message.threads.length + ' Reply'}
                          </DDSTypography.Paragraph>
                        </div>
                      )}
                  </div>
                </div>
                <div>
                  {message.id === showReplyId &&
                    message.type === 'COMMENT' &&
                    message.isActive &&
                    message.threads.length > 0 &&
                    message.threads.map((thread: any, index: any) => {
                      return (
                        <div key={index} className=' px-[28px] w-full h-[50vh]'>
                          <div
                            className={` flex flex-row gap-x-[12px]  py-[12px] px-[12px] rounded-md`}
                            onMouseEnter={() => {
                              setHoveringDiv(thread.id)
                            }}
                            onMouseLeave={() => {
                              setHoveringDiv('')
                            }}
                            style={{
                              backgroundColor:
                                hoveringDiv === thread.id
                                  ? pallete.colors.surface5
                                  : 'white'
                            }}
                          >
                            {message.actionedByUser ? (
                              message.actionedByUser.avatar ? (
                                <img
                                  src={message.actionedByUser?.avatar}
                                  alt='icon'
                                  className='h-[32px] w-[32px] rounded-full'
                                />
                              ) : (
                                <div
                                  className='rounded-full border aspect-square w-[32px] h-[32px] flex items-center justify-center bg-gradient-to-br '
                                  style={{
                                    borderColor: pallete.colors.stroke2
                                  }}
                                >
                                  <DDSTypography.Paragraph
                                    size='para'
                                    variant='bold'
                                    color={pallete.colors.textDark3}
                                    className='text-white text-sm font-semibold'
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {getUserInitials(
                                      message.actionedByUser?.firstName,
                                      message.actionedByUser?.lastName
                                    )}
                                  </DDSTypography.Paragraph>
                                </div>
                              )
                            ) : null}

                            {editableReplyId !== thread.id ? (
                              <div className='flex flex-row justify-between w-full'>
                                <div className='flex flex-col gap-y-[4px] justify-between w-full'>
                                  <div className='flex flex-row w-min items-center gap-x-[8px]'>
                                    <div className='flex justify-start gap-[8px]'>
                                      {message.actionedByUser ? (
                                        <DDSTypography.Title
                                          type='h5'
                                          variant='semiBold'
                                        >
                                          {thread.actionedByUser?.firstName +
                                            ' ' +
                                            thread.actionedByUser?.lastName}
                                        </DDSTypography.Title>
                                      ) : null}

                                      <DDSTypography.Paragraph
                                        size='caption'
                                        variant='medium'
                                        style={{
                                          color: pallete.colors.textDark7
                                        }}
                                        className='whitespace-nowrap'
                                      >
                                        {getTimeForActivity(thread.createdAt)}
                                      </DDSTypography.Paragraph>
                                    </div>
                                    <DDSTypography.Paragraph
                                      size='para'
                                      variant='medium'
                                      style={{
                                        color: pallete.colors.textDark6,
                                        overflowWrap: 'anywhere'
                                      }}
                                    >
                                      {thread.message}
                                    </DDSTypography.Paragraph>
                                  </div>
                                  {hoveringDiv === thread.id &&
                                  thread.type === 'COMMENT' &&
                                  thread.isActive ? (
                                    <div className='flex flex-row gap-x-[10px] items-center'>
                                      <Edit
                                        color={pallete.colors.textDark10}
                                        className={`${
                                          message.actionedByUser?.id ===
                                          currentUserId
                                            ? 'cursor-pointer'
                                            : 'opacity-40 cursor-not-allowed'
                                        }`}
                                        onClick={() => {
                                          if (
                                            message.actionedByUser?.id ===
                                            currentUserId
                                          ) {
                                            handleEditReplyMessage(
                                              thread.id,
                                              thread.message
                                            )
                                          }
                                        }}
                                      />
                                      <TrashCan
                                        color={pallete.colors.textDark10}
                                        className={`${
                                          message.actionedByUser?.id ===
                                          currentUserId
                                            ? 'cursor-pointer'
                                            : 'opacity-40 cursor-not-allowed'
                                        }`}
                                        onClick={() => {
                                          if (
                                            message.actionedByUser?.id ===
                                            currentUserId
                                          ) {
                                            handleDeleteComment(thread)
                                          }
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                                <DDSTypography.Paragraph
                                  size='para'
                                  variant='medium'
                                  style={{
                                    color: pallete.colors.textDark6,
                                    overflowWrap: 'anywhere'
                                  }}
                                >
                                  {thread.message}
                                </DDSTypography.Paragraph>
                              </div>
                            ) : (
                              <TextArea
                                type='secondary'
                                PrimaryButtonText='Save'
                                className={`text-[14px] font-semibold resize-none ${
                                  editableMessageId === thread.id
                                    ? 'p-[12px]'
                                    : ''
                                }`}
                                style={{ color: pallete.colors.textDark5 }}
                                id={thread.id}
                                value={currentEditableReplyMessage}
                                onChange={(value: string) =>
                                  setCurrentEditableReplyMessage(value)
                                }
                                onCancel={handleCancelEditableReplyMessage}
                                primaryButtonOnClick={() =>
                                  saveMessage(thread.id, 'Reply')
                                }
                              />
                            )}
                          </div>
                        </div>
                      )
                    })}
                </div>
                {replyMessageId === message.id && (
                  <TextArea
                    type='secondary'
                    PrimaryButtonText='Reply'
                    onCancel={() => cancelReplyingMessage()}
                    onChange={(value: string) => setCurrentReplyMessage(value)}
                    primaryButtonOnClick={sendReply}
                    loading={loading || store.uiStore.getGlobalLoader()}
                    disabled={loading}
                    value={currentReplyMessage}
                    style={{
                      paddingRight: '156px'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className=' bottom-[0px] flex w-full'>
          <RichTextEditor
            placeholder=' Write your update'
            variant='create'
            onSend={(payload) => {
              setButtonLoading(true)
              addQuill(payload)
              scrollToTop()
            }}
            setMediaFiles={setMediaFiles}
            mediaFiles={mediaFiles}
            payload={payload}
            setPayload={setPayload}
            setPayloadUpdate={payload}
            payloadUpdate={payloadUpdate}
            loading={
              buttonLoading || loading || store.uiStore.getGlobalLoader()
            }
            disabled={loading}
            isPublic={isPublic}
            setIsPublic={setIsPublic}
            assignMemberOptions={assignMemberOptions}
          />
        </div>
      </div>
    </>
  )
}
export default observer(IncidentActivity)
